import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Crown, Leaf, Wind, Star, CircleCheckBig, MapPin, Package, Mail, Phone } from 'lucide-react';

const icons = { premium: Crown, classic: Leaf, light: Wind, special: Star };

const parseLines = (str) =>
  (str || '').split('\n').map((s) => s.trim()).filter(Boolean);

const ProductModal = ({ data }) => {
  const { language, t } = useLanguage();
  const { isDark } = useTheme();
  const key = data?.key || '';
  const IconComponent = icons[key] || Package;
  const isRtl = language === 'ar';
  const phoneNumber = t('contact.phone.number');
  const emailAddress = t('contact.email.address');
  const phoneHref = `tel:${phoneNumber.replace(/[^+0-9]/g, '')}`;

  const heroImage  = data?.image_url || '';
  const title      = data?.title || '';
  const description = data?.content?.trim() ? data.content : '';
  const badge      = data?.badge?.trim()   ? data.badge   : '';
  const tagline    = data?.tagline?.trim() ? data.tagline : '';
  const features   = data?.features?.length > 0 ? data.features : [];
  const variants   = data?.variants?.length > 0 ? data.variants : [];
  const origins    = data?.origins?.length  > 0 ? data.origins  : [];

  const hasVariants = variants.length > 0;
  const hasOrigins  = origins.length  > 0;

  return (
    <div className="flex flex-col">

      {/* ══════════════════════════════════════════════
          TOP HEADER — dark strip with title + badge
      ══════════════════════════════════════════════ */}
      <div
        className={`sticky top-0 z-10 px-6 py-4 flex items-center gap-3 ${
          isRtl ? 'flex-row-reverse' : ''
        } ${isDark ? 'bg-[#0A0A0A]' : 'bg-stone-950'}`}
      >
        {/* Icon */}
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-sm bg-[var(--gold-primary)]/15 border border-[var(--gold-primary)]/25">
          <IconComponent className="w-4.5 h-4.5 text-[var(--gold-primary)]" aria-hidden="true" />
        </div>

        {/* Badge + Title */}
        <div className={`flex-1 min-w-0 ${isRtl ? 'text-right' : 'text-left'}`}>
          <span className="block text-[var(--gold-primary)] text-[10px] font-bold uppercase tracking-[0.2em] leading-none mb-1">
            {badge}
          </span>
          <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-tight truncate">
            {title}
          </h2>
        </div>

        {/* Gold accent rule on the right side of header */}
        <div className={`hidden sm:block w-12 h-px flex-shrink-0 ${isRtl ? 'bg-gradient-to-l from-[var(--gold-primary)]/60 to-transparent' : 'bg-gradient-to-r from-[var(--gold-primary)]/60 to-transparent'}`} />
      </div>

      {/* Thin gold separator line under header */}
      <div className={`h-[1.5px] ${isRtl ? 'bg-gradient-to-l from-[var(--gold-primary)]/70 via-[var(--gold-primary)]/20 to-transparent' : 'bg-gradient-to-r from-[var(--gold-primary)]/70 via-[var(--gold-primary)]/20 to-transparent'}`} />

      {/* ══════════════════════════════════════════════
          BODY — image left, content right (page scrolls)
      ══════════════════════════════════════════════ */}
      <div
        className={`flex flex-col ${
          isRtl ? 'md:flex-row-reverse' : 'md:flex-row'
        }`}
      >
        {/* ── LEFT: clean product image, full and uncropped ── */}
        <div
          className={`md:w-[40%] flex items-center justify-center
            h-64 sm:h-80 md:min-h-[480px]
            ${isDark ? 'bg-[#0D0D0D]' : 'bg-stone-950'}`}
        >
          {heroImage ? (
            <div className="w-full h-full flex items-center justify-center p-6">
              <img
                src={heroImage}
                alt={title}
                className="max-w-full max-h-full object-contain drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.7))' }}
              />
            </div>
          ) : (
            /* placeholder when no image uploaded */
            <div className="flex flex-col items-center justify-center gap-3 opacity-20">
              <IconComponent className="w-16 h-16 text-[var(--gold-primary)]" />
            </div>
          )}
        </div>

        {/* Thin gold vertical separator */}
        <div
          className={`hidden md:block w-[1.5px] bg-gradient-to-b from-transparent via-[var(--gold-primary)]/40 to-transparent`}
        />

        {/* ── RIGHT: all product details ── */}
        <div
          className={`flex-1 ${
            isDark ? 'bg-[#111111]' : 'bg-white'
          }`}
        >
          <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>

            {/* Tagline */}
            <p className={`text-xs italic mb-5 ${isDark ? 'text-white/45' : 'text-gray-400'}`}>
              {tagline}
            </p>

            {/* Gold divider */}
            <div
              className={`h-px mb-5 ${
                isRtl
                  ? 'bg-gradient-to-l from-[var(--gold-primary)]/50 to-transparent'
                  : 'bg-gradient-to-r from-[var(--gold-primary)]/50 to-transparent'
              }`}
            />

            {/* Description */}
            <p
              className={`text-sm leading-relaxed mb-7 ${
                isDark ? 'text-white/72' : 'text-gray-600'
              }`}
            >
              {description}
            </p>

            {/* Key Features */}
            {features.length > 0 && (
              <div className="mb-7">
                <h4
                  className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3.5 flex items-center gap-2 ${
                    isRtl ? 'flex-row-reverse' : ''
                  }`}
                >
                  <CircleCheckBig className="w-3.5 h-3.5" aria-hidden="true" />
                  {language === 'ar' ? 'المميزات الرئيسية' : 'Key Features'}
                </h4>
                <ul className="space-y-2.5">
                  {features.map((f, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                      <span className="mt-[6px] flex-shrink-0 w-4 h-4 rounded-full border border-[var(--gold-primary)]/40 flex items-center justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)]" />
                      </span>
                      <span
                        className={`text-sm leading-snug ${
                          isDark ? 'text-white/70' : 'text-gray-600'
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Variants + Origins */}
            {(hasVariants || hasOrigins) && (
              <div
                className={`grid gap-6 mb-7 ${
                  hasVariants && hasOrigins ? 'grid-cols-2' : 'grid-cols-1'
                }`}
              >
                {hasVariants && (
                  <div>
                    <h4
                      className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3 flex items-center gap-2 ${
                        isRtl ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <Package className="w-3.5 h-3.5" aria-hidden="true" />
                      {language === 'ar' ? 'الأصناف المتاحة' : 'Variants'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {variants.map((v, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2.5 py-1 rounded-sm border ${
                            isDark
                              ? 'bg-white/[0.04] border-white/10 text-white/65'
                              : 'bg-gray-50 border-gray-200 text-gray-600'
                          }`}
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hasOrigins && (
                  <div>
                    <h4
                      className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3 flex items-center gap-2 ${
                        isRtl ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                      {language === 'ar' ? 'مناطق المنشأ' : 'Origins'}
                    </h4>
                    <ul className="space-y-1.5">
                      {origins.map((o, i) => (
                        <li
                          key={i}
                          className={`flex items-center gap-2 text-sm ${
                            isDark ? 'text-white/60' : 'text-gray-500'
                          } ${isRtl ? 'flex-row-reverse' : ''}`}
                        >
                          <span className="w-1 h-1 rounded-full bg-[var(--gold-primary)]/60 flex-shrink-0" />
                          {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* CTA */}
            <div
              className={`pt-6 border-t ${
                isDark ? 'border-white/[0.08]' : 'border-gray-100'
              } flex flex-wrap items-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <a
                href={`mailto:${emailAddress}`}
                className={`btn-shine inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--gold-primary)] text-black font-bold text-xs uppercase tracking-[0.12em] rounded-sm hover:bg-[var(--gold-hover)] transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                {language === 'ar' ? 'طلب الكتالوج' : 'Request Catalogue'}
              </a>
              <a
                href={phoneHref}
                className={`inline-flex items-center gap-2 text-sm font-medium hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''} ${
                  isDark ? 'text-white/45' : 'text-gray-400'
                }`}
                dir="ltr"
              >
                <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                {phoneNumber}
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
